import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable, ErrorHandler } from '@angular/core';
import { IApiResponse, IFilterPaginationDto } from '../models';
import { KeyValue } from '@angular/common';
import {
  IMenuAddonDto,
  IMenuAddonGroupDto,
  IMenuCategoryDto,
  IMenuImageTagDto,
  IMenuItemsDto,
  IMenuPriceAdjustmentDto,
  IMenuSubCategoryDto,
  IMenuWidgetDto,
  IModifierGroupDto,
  IModifierMenuDto,
  ISearchMenuDto,
} from '../models/menu.model';

@Injectable()
export class MenuService extends ApiService {
  constructor(http: HttpClient, errorHandler: ErrorHandler) {
    super(http, errorHandler);
  }

  public getCategoryList(
    id: number,
    model: IFilterPaginationDto
  ): Observable<IMenuCategoryDto[]> {
    return this.post(`admin/menu/category/list/${id}`, model);
  }

  public getSubCategoryList(
    id: number,
    model: IFilterPaginationDto
  ): Observable<IMenuSubCategoryDto[]> {
    return this.post(`admin/menu/subcategory/list/${id}`, model);
  }

  public getMenuItemList(
    id: number,
    model: IFilterPaginationDto
  ): Observable<IMenuItemsDto[]> {
    return this.post(`admin/menu/item/list/${id}`, model);
  }

  public getAddonList(
    id: number,
    model: IFilterPaginationDto
  ): Observable<IMenuAddonDto[]> {
    return this.post(`admin/menu/addon/list/${id}`, model);
  }

  public getModifierList(
    id: number,
    model: IFilterPaginationDto
  ): Observable<IModifierGroupDto[]> {
    return this.post(`admin/menu/modifier/list/${id}`, model);
  }

  public getModifierLinkedMenuItems(
    model: KeyValue<number, number>
  ): Observable<IModifierMenuDto[]> {
    return this.post(`admin/menu/modifier/item`, model);
  }

  public updateModifierLinkedMenuItems(
    id: number,
    model: IModifierMenuDto[]
  ): Observable<IApiResponse<boolean>> {
    return this.put(`admin/menu/modifier/item/${id}`, model);
  }

  public saveModifier(
    model: IMenuAddonGroupDto
  ): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/modifier`, model);
  }

  public saveCategory(
    model: IMenuCategoryDto
  ): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/category`, model);
  }

  public saveSubCategory(
    model: IMenuSubCategoryDto
  ): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/subcategory`, model);
  }

  public saveMenuItem(model: IMenuItemsDto): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/item`, model);
  }

  public saveAddon(
    model: IMenuAddonGroupDto
  ): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/addon`, model);
  }

  public copyMenuItem(
    id: number,
    model: KeyValue<boolean, boolean>
  ): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/item/copy/${id}`, model);
  }

  public copyAddon(id: number): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/addon/copy/${id}`, null);
  }

  public changeCategoryStatus(id: number): Observable<IApiResponse<boolean>> {
    return this.put(`admin/menu/category/${id}`, null);
  }

  public changeSubCategoryStatus(
    id: number
  ): Observable<IApiResponse<boolean>> {
    return this.put(`admin/menu/subcategory/${id}`, null);
  }

  public changeMenuItemStatus(id: number): Observable<IApiResponse<boolean>> {
    return this.put(`admin/menu/item/${id}`, null);
  }

  public deleteCategory(id: number): Observable<IApiResponse<boolean>> {
    return this.delete(`admin/menu/category/${id}`);
  }

  public deleteSubCategory(id: number): Observable<IApiResponse<boolean>> {
    return this.delete(`admin/menu/subcategory/${id}`);
  }

  public deleteMenuItem(id: number): Observable<IApiResponse<boolean>> {
    return this.delete(`admin/menu/item/${id}`);
  }

  public deleteAddon(id: number): Observable<IApiResponse<boolean>> {
    return this.delete(`admin/menu/addon/${id}`);
  }

  public deleteModifier(id: number): Observable<IApiResponse<boolean>> {
    return this.delete(`admin/menu/modifier/${id}`);
  }

  public getMenuItems(id: number): Observable<ISearchMenuDto[]> {
    return this.get(`admin/menu/item/${id}`);
  }

  public getAddons(id: number): Observable<IMenuAddonDto[]> {
    return this.get(`admin/menu/addon/${id}`);
  }

  public verifyAddon(id: number): Observable<KeyValue<string, string>[]> {
    return this.get(`admin/menu/addon/verify/${id}`);
  }

  public getMenuLevel(id: number): Observable<string> {
    return this.get(`admin/menu/level/${id}`);
  }

  public updateMenuLevel(
    model: KeyValue<number, string>
  ): Observable<IApiResponse<boolean>> {
    return this.put(`admin/menu/level`, model);
  }

  public updateMenuOrder(
    id: number,
    model: KeyValue<number, number>[]
  ): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/order/${id}`, model);
  }

  public getMenuItemTags(id: number): Observable<IMenuImageTagDto[]> {
    return this.get(`admin/menu/tag/${id}`);
  }

  public saveMenuItemTags(
    id: number,
    model: IMenuImageTagDto[]
  ): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/tag/${id}`, model);
  }

  public updateMenuTagOrder(
    model: KeyValue<number, number>[]
  ): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/tag/order`, model);
  }

  public getMenuWidget(id: number): Observable<IApiResponse<IMenuWidgetDto>> {
    return this.get(`admin/menu/widget/${id}`);
  }

  public saveMenuWidget(
    id: number,
    model: IMenuWidgetDto
  ): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/widget/${id}`, model);
  }

  public adjustMenuPricing(
    model: IMenuPriceAdjustmentDto
  ): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/adjustment`, model);
  }

  public unbindMenuItemPricing(id: number): Observable<IApiResponse<boolean>> {
    return this.post(`admin/menu/item/unbind/${id}`, null);
  }
}
